<template>
    <!-- Escape and a backdrop click stay enabled. Forcing a choice would be a bigger change than
         this PR intends, and it would strand the user when the save request fails: the buttons
         only close on a 204, and there is no other way out of a modal.
         reserve-mobile-nav-space is off because the pages this renders on have no mobile nav bar
         to sit above. -->
    <BaseDialog v-model:open="showBanner" :title="t('components.cookieBanner.title')" width="narrow" :show-close="false" :reserve-mobile-nav-space="false">
        <p class="details md:body-small text-ink-muted">
            {{ t("components.cookieBanner.body") }}
            <!-- eslint-disable @intlify/vue-i18n/no-raw-text -- punctuation: the period that closes the "Learn more here" sentence -->
            <a class="base-link font-semibold" href="https://www.valence.co/other/privacy#cookies" target="_blank">{{ t("components.cookieBanner.learnMore") }}</a
            >.
            <!-- eslint-enable @intlify/vue-i18n/no-raw-text -- end of the punctuation exemption -->
        </p>
        <template #footer>
            <BaseButton theme="tertiary" data-testid="reject-cookie-button" :disabled="saving" @click="rejectCookies">{{ t("components.cookieBanner.onlyNecessary") }}</BaseButton>
            <BaseButton theme="primary" data-testid="accept-cookie-button" :disabled="saving" @click="acceptAllCookies">{{ t("components.cookieBanner.acceptAll") }}</BaseButton>
        </template>
    </BaseDialog>
</template>

<script setup>
import { getCookie } from "/js/utils.js";
import { usePage } from "@inertiajs/vue3";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import { announceCookieConsentChange, rememberBrowserCookieChoice } from "~vue/utils/cookieConsent";
import { refreshIntercomMessengerForConsentChange } from "~vue/utils/intercom";
import { onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const showBanner = ref(false);
const accountUuid = () => usePage().props?.user_details?.uuid ?? null;

onMounted(async () => {
    // Native apps don't need cookie consent banners — privacy is handled
    // via App Store privacy labels and iOS App Tracking Transparency.
    if (usePage().props?.is_mobile_app) {
        return;
    }
    const cookieConsent = localStorage.getItem("cookie_consent");
    if (cookieConsent === "persisted") {
        return;
    }

    // A choice made before signing in waits in localStorage; once the visitor has an account,
    // carry it over through the same CSRF-protected PUT the buttons use.
    if (cookieConsent && !usePage().props?.user_details?.is_anonymous) {
        const pending = new URLSearchParams(cookieConsent);
        const pendingConsentAt = pending.get("consent_at");
        // An undated (older-format or damaged) marker must never outrank a dated account choice.
        const consentAt = pendingConsentAt && !Number.isNaN(Date.parse(pendingConsentAt)) ? pendingConsentAt : new Date(0).toISOString();
        const response = await putPreferences(pending.get("consent_choice"), pending.get("consent_method") || "banner", consentAt);
        if (!response) {
            return;
        }
        // A newer choice (409 from the server, or another tab saving while this request was in
        // flight) wins: the pre-login one is dropped rather than recorded as this browser's choice.
        const superseded = response.status === 409 || localStorage.getItem("cookie_consent") !== cookieConsent;
        localStorage.setItem("cookie_consent", "persisted");
        if (!superseded) {
            rememberBrowserCookieChoice(accountUuid(), pending.get("consent_choice"));
            announceCookieConsentChange();
        }
        refreshIntercomMessengerForConsentChange();
        return;
    }

    const response = await fetch("/accounts/cookie-preferences/", {
        method: "GET",
        headers: { "X-CSRFToken": getCookie("csrftoken") },
    });
    if (response.status != 200) {
        console.error("Failed to get cookie preferences");
        return;
    }
    const data = await response.json();
    if (!data.cookie_preferences) {
        showBanner.value = true;
        return;
    }

    if (!cookieConsent) {
        localStorage.setItem("cookie_consent", "persisted");
    }
});

const saving = ref(false);

// `consentAt` is only sent when carrying over a choice made before login, so the account keeps
// the day the visitor actually chose rather than the day they signed in.
const putPreferences = async (choice, method, consentAt = null) => {
    const response = await fetch("/accounts/cookie-preferences/", {
        method: "PUT",
        body: JSON.stringify(consentAt ? { choice, method, consent_at: consentAt } : { choice, method }),
        headers: { "X-CSRFToken": getCookie("csrftoken"), "Content-Type": "application/json" },
    });
    if (![200, 204, 409].includes(response.status)) {
        console.error("Failed to update cookie preferences");
        return null;
    }
    return response;
};

const updatePreferences = async (choice) => {
    // Both buttons are disabled while a save is pending, so two clicks cannot race and let an
    // earlier "Accept All" land after a later "Only Necessary".
    if (saving.value) {
        return null;
    }
    saving.value = true;
    const response = await putPreferences(choice, "banner").finally(() => {
        saving.value = false;
    });
    if (response?.status !== 200 && response?.status !== 204) {
        return null;
    }
    // A signed-in user's choice is already on the account; only a visitor's choice waits here
    // to be carried over after login (a waiting non-"all" choice also keeps support chat off),
    // stamped with the server's clock so it is ordered against account choices on one clock.
    const signedIn = !usePage().props?.user_details?.is_anonymous;
    if (signedIn) {
        rememberBrowserCookieChoice(accountUuid(), choice);
        localStorage.setItem("cookie_consent", "persisted");
    } else {
        const consentAt = response.status === 200 ? (await response.json())?.consent_at : null;
        const pending = new URLSearchParams({ consent_method: "banner", consent_choice: choice, consent_at: consentAt ?? new Date().toISOString() });
        localStorage.setItem("cookie_consent", `?${pending}`);
    }
    announceCookieConsentChange();
    refreshIntercomMessengerForConsentChange();
    return response;
};

async function rejectCookies() {
    const response = await updatePreferences("performance");
    if (response) {
        showBanner.value = false;
    }
}

async function acceptAllCookies() {
    const response = await updatePreferences("all");
    if (response) {
        showBanner.value = false;
    }
}
</script>
